import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { ContentTile } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { SlideType } from '@as-react/common-types';
import { Carousel, CarouselContent, CarouselSlot, generateBreakpointsMap } from '@yonderland/andes-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { getPictureSrcset } from '../../util/ImageUtil';
import { generateTestId } from '../../util/TestUtil';
const MINIMUM_AMOUNT_OF_SLIDES_TO_REMOVE_LARGE_RENDITIONS_FOR = 2;
const LARGE_RENDITION_THRESHOLD = 600;
const getGapLength = (gap) => {
    if (typeof gap === 'number' || !Number.isNaN(Number(gap))) {
        return `${Number(gap) / 4}rem`;
    }
    return gap;
};
export const getSlideSizeWithGap = (slides, gap = 4) => `calc((100% - (${(slides - 1).toFixed(4)} * ${getGapLength(gap)})) / ${slides.toFixed(4)})`;
const getContentTileSrcSet = (image, imageCdnPrefix, slidesToShowDesktop = 1) => {
    const srcSet = getPictureSrcset(image, imageCdnPrefix);
    if (slidesToShowDesktop <= MINIMUM_AMOUNT_OF_SLIDES_TO_REMOVE_LARGE_RENDITIONS_FOR) {
        return srcSet;
    }
    return Object.fromEntries(Object.entries(srcSet).filter(([key]) => {
        const parsedKey = parseInt(key);
        return isNaN(parsedKey) || parsedKey < LARGE_RENDITION_THRESHOLD;
    }));
};
export const CarouselComponent = (_a) => {
    var { slides, id, slidesPerView } = _a, carouselProps = __rest(_a, ["slides", "id", "slidesPerView"]);
    const { t } = useTranslation([I18N_NAMESPACES.general]);
    const { imageCdnPrefix } = useAemConfig();
    const contentTileTranslations = {
        readMore: t(`${I18N_NAMESPACES.general}:readMore`),
    };
    const testId = `carousel-${id}`;
    const slideSizeMap = generateBreakpointsMap(slidesPerView);
    const slideSize = Object.values(slideSizeMap).some(size => typeof size === 'string')
        ? slideSizeMap
        : Object.fromEntries(Object.entries(slideSizeMap).map(([key, value]) => [key, getSlideSizeWithGap(value)]));
    const renderSlide = useCallback((_a, index) => {
        var { type } = _a, slideProps = __rest(_a, ["type"]);
        switch (type) {
            case SlideType.CONTENT_TILE: {
                const { image } = slideProps, contentTileProps = __rest(slideProps, ["image"]);
                return (_jsx(CarouselSlot, { children: _jsx(ContentTile, Object.assign({}, contentTileProps, { translations: contentTileTranslations, image: !(image === null || image === void 0 ? void 0 : image.imagePath)
                            ? { src: '' }
                            : {
                                src: image.imagePath,
                                srcset: getContentTileSrcSet(image, imageCdnPrefix, slidesPerView.lg),
                                isPicture: true,
                                lazy: true,
                            }, withoutSpacing: true })) }, generateTestId({ testId, suffix: `slide-${index}` })));
            }
            default:
                return null;
        }
    }, [slides, id]);
    return (_jsx(Carousel, Object.assign({ testId: testId, slideSize: slideSize }, carouselProps, { children: _jsx(CarouselContent, { children: slides.map(renderSlide) }) })));
};
export default CarouselComponent;
